import { useMemo, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Plus, Sparkles, Code2, Wand2, Telescope, Bug, Boxes, Settings as SettingsIcon, PanelLeftClose, PanelLeftOpen, MoreHorizontal, Pencil, Archive, Trash2, ArchiveRestore, EyeOff } from 'lucide-react';
import type { AgentKind } from '@/lib/agents/types';
import { useSessionStore } from '@/lib/store/sessionStore';
import { useUIStore } from '@/lib/store/uiStore';
import { deleteBackendSession } from '@/lib/providers/BackendCliAdapter';
import { cn, formatTime } from '@/lib/utils';

const ICONS: Record<AgentKind, React.ComponentType<{ className?: string }>> = {
  claude_cli: Sparkles,
  codex_cli: Code2,
  gemini_cli: Telescope,
  hermes: Wand2,
  custom_cli: Boxes,
  claude: Sparkles,
  codex: Code2,
  research: Telescope,
  debug: Bug,
  custom: Boxes,
};

const STATUS_DOT: Record<string, string> = {
  idle: 'bg-fg-subtle',
  thinking: 'bg-info animate-pulse-soft',
  streaming: 'bg-info animate-pulse-soft',
  tool_calling: 'bg-accent animate-pulse-soft',
  waiting_for_user: 'bg-warning',
  error: 'bg-danger',
  completed: 'bg-success',
};

export function SessionRail() {
  const sessions = useSessionStore((s) => s.sessions);
  const activeId = useSessionStore((s) => s.activeId);
  const setActive = useSessionStore((s) => s.setActive);
  const newSession = useSessionStore((s) => s.newSession);
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);
  const open = useUIStore((s) => s.railOpen);
  const setOpen = useUIStore((s) => s.setRailOpen);

  // Show archived sessions in a separate, collapsed group at the bottom
  // of the active list when toggled.
  const [showArchived, setShowArchived] = useState(false);
  const { active, archived } = useMemo(() => {
    const a: typeof sessions = [];
    const z: typeof sessions = [];
    for (const s of sessions) (s.archived ? z : a).push(s);
    return { active: a, archived: z };
  }, [sessions]);

  if (!open) {
    return (
      <aside className="flex h-full w-9 shrink-0 flex-col border-r border-border bg-rail py-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="focus-ring mx-auto flex h-7 w-7 items-center justify-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg"
          title="Open sessions"
          aria-label="Open sessions"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
        <div className="mx-auto mt-3 flex h-6 w-6 items-center justify-center rounded-md bg-accent text-accent-fg">
          <span className="text-xs font-bold">X</span>
        </div>
        <nav className="mt-3 flex flex-1 flex-col items-center gap-1.5 overflow-y-auto px-1 pb-2">
          {active.map((s) => {
            const Icon = ICONS[s.agentKind];
            const isActive = s.id === activeId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                className={cn(
                  'focus-ring flex h-7 w-7 items-center justify-center rounded-md',
                  isActive ? 'bg-surface-2 text-fg shadow-soft' : 'text-fg-muted hover:bg-surface-2 hover:text-fg'
                )}
                title={s.name}
                aria-label={s.name}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => newSession('claude_cli')}
            className="focus-ring flex h-7 w-7 items-center justify-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg"
            title="New session"
            aria-label="New session"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </nav>
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="focus-ring mx-auto flex h-7 w-7 items-center justify-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg"
          title="Settings"
          aria-label="Settings"
        >
          <SettingsIcon className="h-3.5 w-3.5" />
        </button>
      </aside>
    );
  }

  return (
    <aside className="flex h-full w-56 shrink-0 flex-col border-r border-border bg-rail">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent text-accent-fg">
          <span className="text-xs font-bold">X</span>
        </div>
        <span className="text-[13px] font-semibold tracking-tight">Xoro</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="focus-ring ml-auto rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"
          aria-label="Collapse sessions"
          title="Collapse sessions"
        >
          <PanelLeftClose className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex items-center justify-between px-3 pb-1 pt-3 text-2xs uppercase tracking-wide text-fg-subtle">
        <span>Sessions</span>
        <button
          onClick={() => newSession('claude_cli')}
          className="focus-ring flex items-center gap-0.5 rounded p-0.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
          aria-label="New session"
          title="New session"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-3">
        <ul className="space-y-0.5">
          {active.map((s) => (
            <SessionRow key={s.id} session={s} isActive={s.id === activeId} />
          ))}
        </ul>

        {archived.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="focus-ring flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-2xs uppercase tracking-wide text-fg-subtle hover:bg-surface-2 hover:text-fg"
            >
              <EyeOff className="h-3 w-3" />
              <span className="flex-1">Archived</span>
              <span className="tabular-nums">{archived.length}</span>
            </button>
            {showArchived && (
              <ul className="mt-0.5 space-y-0.5">
                {archived.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    isActive={s.id === activeId}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </nav>

      <div className="flex items-center gap-1 border-t border-border p-2 text-2xs text-fg-subtle">
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="focus-ring flex flex-1 items-center gap-1.5 rounded-md px-2 py-1 text-left hover:bg-surface-2 hover:text-fg"
          title="Settings"
        >
          <SettingsIcon className="h-3.5 w-3.5" />
          <span>Settings</span>
        </button>
        <span className="tabular-nums px-1">{formatTime(Date.now())}</span>
      </div>
    </aside>
  );
}

interface RowProps {
  session: import('@/lib/agents/types').AgentSession;
  isActive: boolean;
}

/**
 * One session row in the rail. Shows name + status dot + a hover-revealed
 * kebab menu with Rename / Archive / Delete. Double-click on the name
 * triggers inline rename for speed.
 */
function SessionRow({ session, isActive }: RowProps) {
  const setActive = useSessionStore((s) => s.setActive);
  const renameSession = useSessionStore((s) => s.renameSession);
  const archiveSession = useSessionStore((s) => s.archiveSession);
  const restoreSession = useSessionStore((s) => s.restoreSession);
  const removeSession = useSessionStore((s) => s.removeSession);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(session.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const Icon = ICONS[session.agentKind];

  const startRename = () => {
    setDraft(session.name);
    setEditing(true);
    queueMicrotask(() => inputRef.current?.select());
  };

  const commitRename = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== session.name) {
      renameSession(session.id, draft.trim());
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete session "${session.name}"? The CLI process will be killed and chat history will be removed.`)) return;
    void deleteBackendSession(session.id);
    removeSession(session.id);
  };

  return (
    <li>
      <div
        className={cn(
          'group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors',
          isActive
            ? 'bg-surface-2 text-fg shadow-soft'
            : 'text-fg-muted hover:bg-surface-2/70 hover:text-fg',
          session.archived && 'opacity-60',
        )}
      >
        <button
          type="button"
          onClick={() => setActive(session.id)}
          onDoubleClick={startRename}
          className="flex flex-1 items-center gap-2 overflow-hidden text-left"
        >
          <Icon className="h-3.5 w-3.5 shrink-0 text-fg-muted" />
          {editing ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
              }}
              onClick={(e) => e.stopPropagation()}
              className="focus-ring min-w-0 flex-1 rounded border border-border bg-canvas px-1.5 py-0.5 text-[12.5px] text-fg"
            />
          ) : (
            <span className="truncate">{session.name}</span>
          )}
          {!editing && (
            <span
              className={cn('status-dot ml-auto', STATUS_DOT[session.status] ?? 'bg-fg-subtle')}
            />
          )}
        </button>

        {!editing && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="focus-ring rounded p-0.5 text-fg-subtle opacity-0 transition-opacity hover:bg-surface-2 hover:text-fg group-hover:opacity-100 data-[state=open]:opacity-100"
                aria-label="Session actions"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="start"
                sideOffset={4}
                className="z-50 w-[160px] rounded-lg border border-border bg-canvas p-1 shadow-menu animate-fade-in"
              >
                <DropdownMenu.Item
                  className="menu-item"
                  onSelect={() => queueMicrotask(startRename)}
                >
                  <Pencil className="h-3.5 w-3.5 text-fg-muted" />
                  <span>Rename</span>
                </DropdownMenu.Item>
                {session.archived ? (
                  <DropdownMenu.Item
                    className="menu-item"
                    onSelect={() => restoreSession(session.id)}
                  >
                    <ArchiveRestore className="h-3.5 w-3.5 text-fg-muted" />
                    <span>Restore</span>
                  </DropdownMenu.Item>
                ) : (
                  <DropdownMenu.Item
                    className="menu-item"
                    onSelect={() => archiveSession(session.id)}
                  >
                    <Archive className="h-3.5 w-3.5 text-fg-muted" />
                    <span>Archive</span>
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
                <DropdownMenu.Item
                  className="menu-item text-danger"
                  onSelect={onDelete}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span>Delete</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
    </li>
  );
}
